/**
 * Date arithmetic for market data.
 *
 * Everything here works in **whole UTC days since the epoch** and `yyyymm`
 * integers, never a local `Date`. That is deliberate: vitest pins
 * `TZ=America/New_York`, and `new Date("2026-08-01")` is midnight UTC, which
 * reads back as July 31 in every US zone. The same trap the repo documents for
 * `parseDisplayDate` in `lib/format.ts`.
 *
 * This file survives the swap to real data — business-day stepping and
 * peak-hour fractions are needed to interpret the wire, not to fake it.
 */

const MS_PER_DAY = 86_400_000;
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

/** ISO `yyyy-MM-dd` → whole days since epoch. `null` if not that shape. */
export function dayNumber(iso: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const y = Number(iso.slice(0, 4));
  const m = Number(iso.slice(5, 7));
  const d = Number(iso.slice(8, 10));
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  return Math.round(Date.UTC(y, m - 1, d) / MS_PER_DAY);
}

/** Day number → ISO `yyyy-MM-dd`. */
export function isoFromDay(day: number): string {
  return new Date(day * MS_PER_DAY).toISOString().slice(0, 10);
}

/** Today, as a UTC day: `yyyy-MM-dd`. For a default before the topology names the newest assessment. */
export function todayIso(): string {
  return isoFromDay(Math.floor(Date.now() / MS_PER_DAY));
}

/** Day number → `yyyymm` as a single integer (2026-09-04 → 202609). */
export function periodOfDay(day: number): number {
  const d = new Date(day * MS_PER_DAY);
  return d.getUTCFullYear() * 100 + (d.getUTCMonth() + 1);
}

/** Add months to a `yyyymm`, rolling the year. */
export function addMonths(period: number, n: number): number {
  const y = Math.floor(period / 100);
  const m = (period % 100) - 1 + n;
  return (y + Math.floor(m / 12)) * 100 + ((((m % 12) + 12) % 12) + 1);
}

/** Whole months between two `yyyymm` values (`b - a`). */
export function monthsBetween(a: number, b: number): number {
  const ay = Math.floor(a / 100);
  const by = Math.floor(b / 100);
  return (by - ay) * 12 + ((b % 100) - (a % 100));
}

/** `yyyymm` → ISO first-of-month, the convention `IndexPoint.period_start` uses. */
export function periodToIso(period: number): string {
  const m = period % 100;
  return `${Math.floor(period / 100)}-${m < 10 ? "0" : ""}${m}-01`;
}

/** `yyyymm` → "Mar 28". */
export function periodLabel(period: number): string {
  return `${MONTHS[(period % 100) - 1]} ${String(Math.floor(period / 100)).slice(2)}`;
}

/** 0 = Sunday. */
export function weekdayOf(day: number): number {
  return (((day + 4) % 7) + 7) % 7;
}

/**
 * The six NERC holidays for a year, as day numbers.
 *
 * Observance rule matches the legacy PowerUp SQL: a Sunday holiday moves to
 * Monday, a Saturday holiday does NOT move (it is already off-peak).
 */
export function nercHolidays(year: number): Set<number> {
  const out = new Set<number>();
  const at = (m: number, d: number) => Math.round(Date.UTC(year, m, d) / MS_PER_DAY);

  const shiftSunday = (day: number) => (weekdayOf(day) === 0 ? day + 1 : day);
  out.add(shiftSunday(at(0, 1))); // New Year's Day
  out.add(shiftSunday(at(6, 4))); // Independence Day
  out.add(shiftSunday(at(11, 25))); // Christmas Day

  // Memorial Day — last Monday in May.
  let memorial = at(4, 31);
  while (weekdayOf(memorial) !== 1) memorial -= 1;
  out.add(memorial);

  // Labor Day — first Monday in September.
  let labor = at(8, 1);
  while (weekdayOf(labor) !== 1) labor += 1;
  out.add(labor);

  // Thanksgiving — fourth Thursday in November.
  let thanksgiving = at(10, 1);
  while (weekdayOf(thanksgiving) !== 4) thanksgiving += 1;
  thanksgiving += 21;
  out.add(thanksgiving);

  return out;
}

const holidayCache = new Map<number, Set<number>>();

export function isNercHoliday(day: number): boolean {
  const year = new Date(day * MS_PER_DAY).getUTCFullYear();
  let set = holidayCache.get(year);
  if (!set) {
    set = nercHolidays(year);
    holidayCache.set(year, set);
  }
  return set.has(day);
}

/**
 * Mon–Fri and not a NERC holiday. Assessments only publish on these.
 *
 * Note `isNercHoliday` returns true for a Saturday holiday (Jul 4 2026), which
 * is correct for this predicate but would wrongly exclude that Saturday from
 * CAISO's Mon–Sat on-peak block. `onPeakFraction` is a flat monthly average
 * today so nothing reads it that way; an hour-level peak calendar must apply
 * the observance rule itself rather than reusing this function.
 */
export function isBusinessDay(day: number): boolean {
  const w = weekdayOf(day);
  return w !== 0 && w !== 6 && !isNercHoliday(day);
}

export function previousBusinessDay(day: number): number {
  let d = day - 1;
  while (!isBusinessDay(d)) d -= 1;
  return d;
}

export function lastBusinessDayOnOrBefore(day: number): number {
  let d = day;
  while (!isBusinessDay(d)) d -= 1;
  return d;
}

/** Ascending, inclusive. */
export function businessDays(fromDay: number, toDay: number): number[] {
  const out: number[] = [];
  for (let d = fromDay; d <= toDay; d += 1) if (isBusinessDay(d)) out.push(d);
  return out;
}

/**
 * On-peak hour fraction for an ISO.
 *
 * CAISO runs a 6x16 block (Mon–Sat), everyone else 5x16 (Mon–Fri). Expressed
 * as a fraction of all hours in the month, which is what blends on/off-peak
 * into ATC: `atc = on * f + off * (1 - f)`.
 */
export function onPeakFraction(iso: string): number {
  return iso === "CAISO" ? 0.41 : 0.34;
}
